<template>
    <div class="wan-model-page">
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('wan.generatorTitle') }}</h1>
                            <p class="generator-description">
                                {{ t('wan.generatorDescription')
                                }}<span class="highlight-red">{{
                                    t('wan.generatorDescriptionHighlight1')
                                    }}</span>{{ t('wan.generatorDescriptionText2')
                                    }}<span class="highlight-red">{{
                                    t('wan.generatorDescriptionHighlight2')
                                    }}</span>{{ t('wan.generatorDescriptionEnd') }}
                            </p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Wan" :tabs="tabs" redirect-url="/ai-video" @generate="handleGenerate"
                        model-key="WAN_2_5">
                        <template #model-logo>
                            <div class="wan-logo">W</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Native Audio Integration Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wan.nativeAudioIntegrationTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan.nativeAudioIntegrationDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.nativeAudioIntegration"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.nativeAudioIntegration
                                        )
                                    "
                                    :title="t('wan.nativeAudioIntegrationTitle')"
                                    :description="
                                        t('wan.nativeAudioIntegrationDescription')
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Enhanced Dynamic Effects Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedDynamicEffects"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.enhancedDynamicEffects
                                        )
                                    "
                                    :title="t('wan.enhancedDynamicEffectsTitle')"
                                    :description="
                                        t('wan.enhancedDynamicEffectsDescription')
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wan.enhancedDynamicEffectsTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan.enhancedDynamicEffectsDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Precise Camera Control Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wan.preciseCameraControlTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t('wan.preciseCameraControlDescription')
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.preciseCameraControl"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.preciseCameraControl
                                        )
                                    "
                                    :title="t('wan.preciseCameraControlTitle')"
                                    :description="
                                        t('wan.preciseCameraControlDescription')
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Enhanced Visual Realism Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedVisualRealism"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.enhancedVisualRealism
                                        )
                                    "
                                    :title="t('wan.enhancedVisualRealismTitle')"
                                    :description="
                                        t('wan.enhancedVisualRealismDescription')
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wan.enhancedVisualRealismTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan.enhancedVisualRealismDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Improved Context Understanding Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'wan.improvedContextUnderstandingTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan.improvedContextUnderstandingDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.improvedContextUnderstanding"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.improvedContextUnderstanding
                                        )
                                    "
                                    :title="
                                        t(
                                            'wan.improvedContextUnderstandingTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'wan.improvedContextUnderstandingDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('wan.howToUseTitle') }}</h2>
                </div>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan.step1Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan.step1Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan.step2Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan.step3Description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <section class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('wan.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('wan.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan.sora2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/veo/veo-3-1" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                :alt="t('wan.veo31Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan.veo31Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/seedance" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/98653aa87810721ed41e47d1df57b068.png"
                                :alt="t('wan.seedanceTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan.seedanceTitle') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('wan.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <!-- <img src="/weryai_48.png" alt="微鲤AI" class="logo-image" /> -->
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('wan.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('wan.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('wan.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('wan.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { getResourceThumbnail } from '~/utils/common'

definePageMeta({
    layout: 'website',
})

// 多语言支持
const { t } = useI18n()

const demoVideos = {
    nativeAudioIntegration:
        'https://static.weryai.com/growth/video/48c3f8b6f46fab93c21420d85a606452.mp4',
    enhancedDynamicEffects:
        'https://static.weryai.com/growth/video/91ee91dc8cfd655c34b7863355a6e9e5.mp4',
    preciseCameraControl:
        'https://static.weryai.com/growth/video/c1a4f4e62dd53d26673987996b994d40.mp4',
    enhancedVisualRealism:
        'https://static.weryai.com/growth/video/47ec69f757d791be7962f317b5abd97f.mp4',
    improvedContextUnderstanding:
        'https://static.weryai.com/growth/video/aa5716f303604cc2eb64505e4d718c11.mp4',
} as const

// 使用多语言SEO系统
useMultilingualSeo({
    pageType: 'wan_ai',
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Tabs configuration
const tabs = computed(() => [
    { name: t('wan.tabImageToVideo'), external: false },
    { name: t('wan.tabTextToVideo'), external: false },
])

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('wan.step1Title'),
        description: t('wan.step1Description'),
    },
    {
        title: t('wan.step2Title'),
        description: t('wan.step2Description'),
    },
    {
        title: t('wan.step3Title'),
        description: t('wan.step3Description'),
    },
])

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Wan AI Video 1',
        embedUrl: 'https://www.youtube.com/embed/2YEvXE4t8FM',
    },
    {
        title: 'Wan AI Video 2',
        embedUrl: 'https://www.youtube.com/embed/CfdyO2ikv88',
    },
    {
        title: 'Wan AI Video 3',
        embedUrl: 'https://www.youtube.com/embed/hQtuXcUZR40',
    },
    {
        title: 'Wan AI Video 4',
        embedUrl: 'https://www.youtube.com/embed/dzmjDlRCiP4',
    },
])

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/RajaChichulkar/status/1971032311853707594',
    'https://twitter.com/slantsalot/status/1970758230885232939',
    'https://twitter.com/1littlecoder/status/1970713575447765037',
    'https://twitter.com/techhalla/status/1970951345646846166',
    'https://twitter.com/f_fanshu/status/1950559935038644611',
    'https://twitter.com/VaderResearch/status/1970773935680602143',
    'https://twitter.com/BrentLynch/status/1970684821300207796',
    'https://twitter.com/mar_vn_nv_cie/status/1970836258617532561',
    'https://twitter.com/jossslopez/status/1970918152826748988',
    'https://twitter.com/Lars_pragmata/status/1970893318281199679',
]

// FAQ Data
const faqData = computed(() => [
    {
        question: t('wan.faq1Question'),
        answer: t('wan.faq1Answer'),
    },
    {
        question: t('wan.faq2Question'),
        answer: t('wan.faq2Answer'),
    },
    {
        question: t('wan.faq3Question'),
        answer: t('wan.faq3Answer'),
    },
    {
        question: t('wan.faq4Question'),
        answer: t('wan.faq4Answer'),
    },
    {
        question: t('wan.faq5Question'),
        answer: t('wan.faq5Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'wan_2_5',
        type: 'video',
    })
})

// --- SEO 结构化数据注入 (确保在变量定义之后) ---

const requestUrlHref = useRequestURL().href

// HowTo SEO
useHowToSeo(
    computed(() => t('wan.howToUseTitle')),
    howToUseSteps
)

// 注意：FAQPage 已由 useMultilingualSeo 注入，无需重复调用 useFaqSeo

// Video SEO
useVideoSeo(
    youtubeVideos.value.map(v => ({
        title: v.title,
        embedUrl: v.embedUrl,
    }))
)

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('wan.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'SoftwareApplication',
                    name: t('wan.generatorTitle'),
                    description: t('wan.pageDescription'),
                    applicationCategory: 'MultimediaApplication',
                    operatingSystem: 'Web',
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'USD',
                    },
                    provider: {
                        '@type': 'Organization',
                        name: '微鲤AI',
                        url: 'https://www.weryai.com.cn',
                        logo: 'https://www.weryai.com.cn/logo.png',
                    },
                    featureList: [
                        t('wan.highQualityVideoGenerationTitle'),
                        t('wan.creativeExpressionSupportTitle'),
                        t('wan.multimodalInputSupportTitle'),
                        t('wan.advancedControlOptionsTitle'),
                    ],
                })
            ),
        },
    ],
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
